import axios from 'axios';
import cacheService from './CacheService';
import { setupRetryInterceptor } from '../utils/networkRetry';
import StorageManager from '../utils/StorageManager';
import { notifySectionPaused } from '../componants/ValveSystem/valveEvents';

// Determine base URL based on hostname
const getBaseUrl = () => {
  if (window.location.hostname === 'iburrow.com') {
    return 'http://iburrow.com:2401';
  }
  return 'http://localhost:2401'; // Default for local development
};

// Create API client instance with default config
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || getBaseUrl(),
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Log the API connection configuration (helpful for debugging)
console.log(`[api.js] Connecting to API at: ${apiClient.defaults.baseURL} (Source: ${process.env.REACT_APP_API_BASE_URL ? 'REACT_APP_API_BASE_URL env var' : 'default fallback'})`);

// Request interceptor for adding auth token
apiClient.interceptors.request.use(config => {
  const token = StorageManager.get('burrowtoken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Check cache for GET requests
  if (config.method === 'get' && config.cache !== false) {
    const cacheKey = `${config.url}${JSON.stringify(config.params || {})}`;
    const cachedData = cacheService.get(cacheKey);
    
    if (cachedData) {
      console.log(`[Cache] Using cached data for: ${config.url}`);
      // Return cached data and cancel request
      config.adapter = () => {
        return Promise.resolve({
          data: cachedData,
          status: 200,
          statusText: 'OK',
          headers: {},
          config,
          request: {},
          cached: true
        });
      };
    }
  }
  
  return config;
}, error => {
  return Promise.reject(error);
});

// Response interceptor for caching
apiClient.interceptors.response.use(response => {
  // Add responses to cache for GET requests
  if (response.config.method === 'get' && response.config.cache !== false) {
    // Don't cache failed requests
    if (response.status >= 200 && response.status < 300) {
      const cacheKey = `${response.config.url}${JSON.stringify(response.config.params || {})}`;
      
      // Get custom cache TTL from response headers if available
      let ttl = null;
      const cacheControl = response.headers['cache-control'];
      if (cacheControl) {
        const maxAgeMatch = cacheControl.match(/max-age=(\d+)/);
        if (maxAgeMatch && maxAgeMatch[1]) {
          ttl = parseInt(maxAgeMatch[1], 10) * 1000; // Convert to milliseconds
        }
      }
      
      // Store in cache
      cacheService.set(cacheKey, response.data, ttl);
      console.log(`[Cache] Cached response for: ${response.config.url}`);
    }
  }
  
  return response;
}, error => {
  // Valve system: a paused section 503s non-exempt users → lift to the React
  // layer so the app can send the member to the /status door (never a raw 503).
  if (error.response?.status === 503 && error.response?.data?.sectionPaused === true) {
    try {
      notifySectionPaused(error.response.data);
    } catch (e) { /* non-fatal */ }
  }
  console.error('API Error:', error.response);
  return Promise.reject(error);
});

// Setup automatic retry with exponential backoff (must be added after other interceptors)
setupRetryInterceptor(apiClient, {
  maxRetries: 3,
  retryDelay: 1000,
  maxDelay: 5000
});

// Define API methods with caching options
const api = {
  /**
   * Make a GET request
   * @param {string} url - API endpoint
   * @param {Object} config - Axios config
   * @param {boolean} [config.cache=true] - Whether to use cache
   * @param {number} [config.cacheTTL] - Custom cache TTL in milliseconds
   * @returns {Promise} - Axios promise
   */
  get: (url, config = {}) => apiClient.get(url, config),
  
  /**
   * Make a POST request
   * @param {string} url - API endpoint
   * @param {*} data - Request body
   * @param {Object} config - Axios config
   * @returns {Promise} - Axios promise
   */
  post: (url, data, config = {}) => apiClient.post(url, data, config),
  
  /**
   * Make a PUT request
   * @param {string} url - API endpoint
   * @param {*} data - Request body
   * @param {Object} config - Axios config
   * @returns {Promise} - Axios promise
   */
  put: (url, data, config = {}) => apiClient.put(url, data, config),
  
  /**
   * Make a DELETE request
   * @param {string} url - API endpoint
   * @param {Object} config - Axios config
   * @returns {Promise} - Axios promise
   */
  delete: (url, config = {}) => apiClient.delete(url, config),
  
  /**
   * Make a PATCH request
   * @param {string} url - API endpoint
   * @param {*} data - Request body
   * @param {Object} config - Axios config
   * @returns {Promise} - Axios promise
   */
  patch: (url, data, config = {}) => apiClient.patch(url, data, config),
  
  /**
   * Clear API cache
   */
  clearCache: () => cacheService.clear(),
  
  /**
   * Get API cache statistics
   * @returns {Object} - Cache statistics
   */
  getCacheStats: () => cacheService.getStats()
};

export default api;
